import { useTranslation } from 'react-i18next';
import { Button, Text, LoadingOverlay } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconBuildingStore, IconTrash } from '@tabler/icons-react';
import { Business, Service } from '@turizi/shared';
import { useCreateServiceProvider, useDeleteServiceProvider } from '@/hooks/api-hooks';
import BusinessSelector from './business-selector';
import BusinessCard from './business-card-private';
import { BUSINESSES_PATH } from '@/lib/navigation';
import { memo } from 'react';

interface ServiceProvidersProps {
    service: Service;
}

const ServiceProviders = memo(function ServiceProviders({ service }: ServiceProvidersProps) {
    const { t } = useTranslation();
    const providers = service?.providers || [];

    const { mutate: createServiceProvider, isPending: creating } = useCreateServiceProvider();
    const { mutate: deleteServiceProvider, isPending: deleting } = useDeleteServiceProvider();

    const handleAddProvider = (business: Business) => {
        if (!business || !service) return;

        createServiceProvider({
            service_id: service.id,
            business_id: business.id,
        });
    };

    const handleRemoveProvider = (id: number) => {
        deleteServiceProvider(id);
    };

    return (
        <div>

            <div className="flex items-start gap-8 justify-between ">
                <div className="flex-1">
                    <div className='font-semibold text-gray-800 text-md mb-2'>
                        {t('providers')}
                    </div>
                </div>

                {/* Add Provider Section */}
                <BusinessSelector
                    onSelect={(business: Business) => handleAddProvider(business)}
                    onSelectMultiple={(businesses: Business[]) => {
                        businesses.forEach(handleAddProvider);
                    }}
                    variant="unstyled"
                    exclude={providers.map(p => p.id)}
                    placeholder={
                        <Button
                            size="xs"
                            radius="sm"
                            color="black"
                            loading={creating}
                            leftSection={<IconBuildingStore size={16} stroke={1} />}
                        >
                            {t('add_providers')}
                        </Button>
                    }
                />
            </div>

            {/* Current Providers List */}
            <div className="mt-4">
                {providers.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                        {t('no_providers_assigned')}
                    </Text>
                ) : (
                    <div className="space-y-2">
                        {providers.map((provider, index) => (
                            <div key={provider.id} className="flex justify-between items-center gap-8 solid-box-border">
                                <div className="text-sm text-gray-400">
                                    {index + 1}.
                                </div>
                                <Link
                                    to={`${BUSINESSES_PATH}/${provider.id}`}
                                    className="block flex-1"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <BusinessCard
                                        business={provider}
                                        isLoading={false}
                                        className="bg-white"
                                    />
                                </Link>
                                <Button
                                    size="md"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => handleRemoveProvider(provider.ServiceProvider.id)}
                                    loading={deleting}
                                >
                                    <IconTrash size={14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

export default ServiceProviders;